"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  DifferenceResult,
  MeanResult,
  ProportionResult,
} from "@/components/tools/stats-distributions/ci-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import {
  type MeanSummary,
  summarize,
} from "@/lib/tool-kit/stats-distributions/confidence-interval";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** English strings. A translated content file overrides these through `ui`. */
export const CONFIDENCE_INTERVAL_CALCULATOR_STRINGS = {
  parameterLabel: "Confidence interval for",
  parameterMean: "A mean",
  parameterProportion: "A proportion",
  parameterDifference: "Difference between two means",
  inputLabel: "Enter",
  inputSummary: "Mean, SD and n",
  inputData: "Raw data",
  meanLabel: "Sample mean (x̄)",
  sdLabel: "Sample standard deviation (s)",
  sigmaLabel: "Population standard deviation (σ)",
  nLabel: "Sample size (n)",
  sigmaKnown: "I know the population SD σ (use z instead of t)",
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "58, 71, 64, 66, 59, 73, 62, 69",
  valuesDetected: "one: value read|other: values read",
  successesLabel: "Number of successes (x)",
  trialsLabel: "Sample size (n)",
  group1: "Group 1",
  group2: "Group 2",
  levelLabel: "Confidence level (%)",
  results: "Results",
  interval: "{level}% confidence interval",
  estimate: "Point estimate",
  difference: "Difference (x̄₁ − x̄₂)",
  margin: "Margin of error",
  standardError: "Standard error",
  criticalT: "t* on {df} df",
  criticalZ: "z*",
  workingHeading: "How this was calculated",
  interpretationMean:
    "We are {level}% confident that the population mean lies between {lower} and {upper}. The {level}% describes the method, not this one interval: if you drew many samples and built an interval from each, about {level}% of those intervals would contain the true mean. This particular interval either contains it or it does not.",
  interpretationDifference:
    "We are {level}% confident that the difference between the population means (group 1 − group 2) lies between {lower} and {upper}.",
  excludesZero:
    "The interval excludes 0, so the difference is statistically significant at α = {alpha} (two-sided Welch test).",
  includesZero:
    "The interval includes 0, so a true difference of zero is consistent with these data at α = {alpha}.",
  interpretationProportion:
    "We are {level}% confident that the population proportion lies between {lower} and {upper} (Wilson score interval). As with any confidence interval, the {level}% is the long-run success rate of the method, not a probability about this one interval.",
  methodColumn: "Method",
  lowerColumn: "Lower",
  upperColumn: "Upper",
  widthColumn: "Width",
  methodWald: "Wald (normal approximation)",
  methodWilson: "Wilson score (recommended)",
  methodExact: "Clopper–Pearson (exact)",
  proportionSummary: "p̂ = {x} / {n} = {phat}; z* = {z}",
  clippedNote:
    "The Wald formula ran past 0 or 1 and was cut back to fit — a sign the approximation does not suit these data.",
  waldWarning:
    "Fewer than 10 successes or 10 failures: the Wald interval is unreliable here. Use the Wilson or Clopper–Pearson interval.",
  apaHeading: "APA style",
  apaInterval: "{level}% CI [{lower}, {upper}]",
  copy: "Copy",
  copied: "Copied!",
  enterMean:
    "Enter a mean, a standard deviation greater than 0, and a whole-number sample size of at least 2.",
  enterData: "Paste at least two numbers with some spread between them.",
  enterProportion:
    "Enter whole numbers: successes between 0 and the sample size, and a sample size of at least 1.",
  enterDifference:
    "Enter a mean, SD and sample size (at least 2) for both groups.",
  enterLevel: "Enter a confidence level between 0 and 100, such as 95.",
} as const;

type Strings = typeof CONFIDENCE_INTERVAL_CALCULATOR_STRINGS;
type Parameter = "mean" | "proportion" | "difference";
type InputMode = "summary" | "data";

interface Fields {
  mean: string;
  sd: string;
  n: string;
  data: string;
  sigmaKnown: boolean;
  x: string;
  trials: string;
  mean1: string;
  sd1: string;
  n1: string;
  mean2: string;
  sd2: string;
  n2: string;
}

const INITIAL: Fields = {
  mean: "64.8",
  sd: "9.6",
  n: "25",
  data: "",
  sigmaKnown: false,
  x: "42",
  trials: "120",
  mean1: "78.2",
  sd1: "10.5",
  n1: "30",
  mean2: "72.6",
  sd2: "12.8",
  n2: "28",
};

function readSummary(mean: string, sd: string, n: string): MeanSummary | null {
  const m = parseNumberInput(mean);
  const s = parseNumberInput(sd);
  const size = parseIntegerInput(n);
  if (m === null || s === null || size === null) {
    return null;
  }
  return { mean: m, sd: s, n: size };
}

function SummaryFields({
  prefix,
  mean,
  sd,
  n,
  sdLabel,
  onChange,
  strings,
}: {
  prefix: string;
  mean: string;
  sd: string;
  n: string;
  sdLabel: string;
  onChange: (field: "mean" | "sd" | "n", value: string) => void;
  strings: Strings;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id={`${prefix}-mean`}
        label={strings.meanLabel}
        numeric
        onChange={(value) => onChange("mean", value)}
        value={mean}
      />
      <TextField
        id={`${prefix}-sd`}
        label={sdLabel}
        numeric
        onChange={(value) => onChange("sd", value)}
        value={sd}
      />
      <TextField
        id={`${prefix}-n`}
        label={strings.nLabel}
        numeric
        onChange={(value) => onChange("n", value)}
        value={n}
      />
    </div>
  );
}

function MeanInputs({
  fields,
  mode,
  setMode,
  update,
  strings,
}: {
  fields: Fields;
  mode: InputMode;
  setMode: (mode: InputMode) => void;
  update: (patch: Partial<Fields>) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const count = parseNumberList(fields.data).length;
  return (
    <>
      <PillGroup
        label={strings.inputLabel}
        name="ci-input"
        onChange={setMode}
        options={[
          { value: "summary", label: strings.inputSummary },
          { value: "data", label: strings.inputData },
        ]}
        value={mode}
      />
      {mode === "summary" ? (
        <>
          <SummaryFields
            mean={fields.mean}
            n={fields.n}
            onChange={(field, value) =>
              update({ [field]: value } as Partial<Fields>)
            }
            prefix="ci"
            sd={fields.sd}
            sdLabel={fields.sigmaKnown ? strings.sigmaLabel : strings.sdLabel}
            strings={strings}
          />
          <Checkbox
            checked={fields.sigmaKnown}
            label={strings.sigmaKnown}
            onChange={(sigmaKnown) => update({ sigmaKnown })}
          />
        </>
      ) : (
        <TextAreaField
          hint={`${fmt(count)}${tPlural(strings.valuesDetected, locale, count)}`}
          id="ci-data"
          label={strings.dataLabel}
          onChange={(data) => update({ data })}
          placeholder={strings.dataPlaceholder}
          value={fields.data}
        />
      )}
    </>
  );
}

function ProportionInputs({
  fields,
  update,
  strings,
}: {
  fields: Fields;
  update: (patch: Partial<Fields>) => void;
  strings: Strings;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        id="ci-x"
        label={strings.successesLabel}
        numeric
        onChange={(x) => update({ x })}
        value={fields.x}
      />
      <TextField
        id="ci-trials"
        label={strings.trialsLabel}
        numeric
        onChange={(trials) => update({ trials })}
        value={fields.trials}
      />
    </div>
  );
}

function DifferenceInputs({
  fields,
  update,
  strings,
}: {
  fields: Fields;
  update: (patch: Partial<Fields>) => void;
  strings: Strings;
}) {
  return (
    <>
      <fieldset className="space-y-3">
        <legend className="font-medium text-foreground text-sm">
          {strings.group1}
        </legend>
        <SummaryFields
          mean={fields.mean1}
          n={fields.n1}
          onChange={(field, value) =>
            update({ [`${field}1`]: value } as Partial<Fields>)
          }
          prefix="ci-g1"
          sd={fields.sd1}
          sdLabel={strings.sdLabel}
          strings={strings}
        />
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="font-medium text-foreground text-sm">
          {strings.group2}
        </legend>
        <SummaryFields
          mean={fields.mean2}
          n={fields.n2}
          onChange={(field, value) =>
            update({ [`${field}2`]: value } as Partial<Fields>)
          }
          prefix="ci-g2"
          sd={fields.sd2}
          sdLabel={strings.sdLabel}
          strings={strings}
        />
      </fieldset>
    </>
  );
}

function meanSummaryFor(fields: Fields, mode: InputMode): MeanSummary | null {
  if (mode === "data") {
    const values = parseNumberList(fields.data);
    return values.length >= 2 ? summarize(values) : null;
  }
  return readSummary(fields.mean, fields.sd, fields.n);
}

function CiResults({
  parameter,
  mode,
  fields,
  level,
  strings,
  fmt,
}: {
  parameter: Parameter;
  mode: InputMode;
  fields: Fields;
  level: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (parameter === "proportion") {
    return (
      <ProportionResult
        fmt={fmt}
        level={level}
        n={parseIntegerInput(fields.trials)}
        strings={strings}
        x={parseIntegerInput(fields.x)}
      />
    );
  }
  if (parameter === "difference") {
    return (
      <DifferenceResult
        first={readSummary(fields.mean1, fields.sd1, fields.n1)}
        fmt={fmt}
        level={level}
        second={readSummary(fields.mean2, fields.sd2, fields.n2)}
        strings={strings}
      />
    );
  }
  const prompt = mode === "data" ? strings.enterData : strings.enterMean;
  const summary = meanSummaryFor(fields, mode);
  if (summary === null) {
    return <Notice>{prompt}</Notice>;
  }
  return (
    <MeanResult
      fmt={fmt}
      level={level}
      prompt={prompt}
      sigmaKnown={mode === "summary" && fields.sigmaKnown}
      strings={strings}
      summary={summary}
    />
  );
}

export default function ConfidenceIntervalCalculatorWidget() {
  const strings = useToolUi(CONFIDENCE_INTERVAL_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [parameter, setParameter] = useState<Parameter>("mean");
  const [mode, setMode] = useState<InputMode>("summary");
  const [levelText, setLevelText] = useState("95");
  const [fields, setFields] = useState<Fields>(INITIAL);
  const update = (patch: Partial<Fields>) =>
    setFields((current) => ({ ...current, ...patch }));

  const percent = parseNumberInput(levelText);
  const level =
    percent !== null && percent > 0 && percent < 100 ? percent / 100 : null;

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.parameterLabel}
          name="ci-parameter"
          onChange={setParameter}
          options={[
            { value: "mean", label: strings.parameterMean },
            { value: "proportion", label: strings.parameterProportion },
            { value: "difference", label: strings.parameterDifference },
          ]}
          value={parameter}
        />
        {parameter === "mean" ? (
          <MeanInputs
            fields={fields}
            mode={mode}
            setMode={setMode}
            strings={strings}
            update={update}
          />
        ) : null}
        {parameter === "proportion" ? (
          <ProportionInputs fields={fields} strings={strings} update={update} />
        ) : null}
        {parameter === "difference" ? (
          <DifferenceInputs fields={fields} strings={strings} update={update} />
        ) : null}
        <div className="max-w-xs">
          <TextField
            id="ci-level"
            label={strings.levelLabel}
            numeric
            onChange={setLevelText}
            value={levelText}
          />
        </div>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {level === null ? (
          <Notice>{strings.enterLevel}</Notice>
        ) : (
          <CiResults
            fields={fields}
            fmt={fmt}
            level={level}
            mode={mode}
            parameter={parameter}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}
